import * as React from 'react';
import { Modal, Input, Alert, Form } from 'antd';
import { connect } from 'react-redux';
import PostCompletionQuestionnaire from '../PostCompletionQuestionnaire';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { supplierGetQuestionnaire, supplierSubmitReview } from '../../thunks/work_orders_thunks';
import { isInternalTech } from '../../utils/AuthUtils';
import WorkOrderReviewComponent from '../WorkOrderReviewComponent';
import posthog from 'posthog-js';

const { Item: FormItem } = Form;

interface SupplierApproveReviewWorkFormProps {
	onSubmit: any;
	onCancel: any;
	visible: boolean;
	createErrors: any;
	updateErrors: any;
	creating: boolean;
	updating: boolean;
	workOrderId: number;
	currentUser: any;
	onlyReview: boolean;
	getQuestionnaire: any;
	workOrder: any;
	submitReview: any;
}

const SupplierApproveReviewWorkForm: React.FC<SupplierApproveReviewWorkFormProps> = (props) => {
	const {
		visible,
		onCancel,
		onSubmit,
		workOrderId,
		onlyReview,
		createErrors,
		updateErrors,
		creating,
		updating,
		currentUser,
		getQuestionnaire,
		workOrder,
		submitReview,
	} = props;
	const [form] = Form.useForm();
	const [loaded, setLoaded] = React.useState(false);
	const [questionnaire, setQuestionnaire] = React.useState({});

	const problemTypeId = nullSafeGet('problemTypeId', workOrder);

	React.useEffect(() => {
		getQuestionnaire(problemTypeId)
			.then((data = []) => {
				if (data.length) {
					setQuestionnaire(data[data.length - 1]);
				}
			})
			.finally(() => setLoaded(true));
	}, [getQuestionnaire, problemTypeId]);

	const isThirdPartySupplier = !isInternalTech(currentUser);
	const isAssignedToThirdParty = React.useMemo(() => {
		return nullSafeGetOrElse('supplierFacility.status', workOrder, 'active') !== 'private';
	}, [workOrder]);

	if (!loaded) return null;

	if (nullSafeGet('questions.length', questionnaire)) {
		return (
			<PostCompletionQuestionnaire
				successCb={() => {
					onSubmit({ workOrderId, note: '' });
				}}
				onCancel={onCancel}
				questionsList={questionnaire}
				workOrderId={workOrderId}
				submitting={creating || updating}
			/>
		);
	}
	if (isAssignedToThirdParty && !isThirdPartySupplier)
		return (
			<WorkOrderReviewComponent
				successCb={() => {
					onSubmit({ workOrderId, note: '' });
				}}
				submitting={creating || updating}
				onCancel={onCancel}
				workOrder={workOrder}
				onlyReview={onlyReview}
			/>
		);

	return (
		<Modal
			visible={visible}
			width={600}
			title={isThirdPartySupplier ? 'Complete Work' : onlyReview ? 'Review Work' : 'Approve Work'}
			okText={isThirdPartySupplier ? 'Complete' : onlyReview ? 'Review' : 'Approve'}
			onCancel={onCancel}
			onOk={() => form.submit()}
			closable={false}
			confirmLoading={creating || updating}
		>
			<Form
				form={form}
				layout="vertical"
				className="supplierForm"
				onFinish={(values) => {
					onSubmit({ ...values, workOrderId });
				}}
			>
				{createErrors.length > 0 ? (
					<FormItem>
						<Alert message={createErrors.join(' ')} type="error" />
					</FormItem>
				) : null}
				{updateErrors.length > 0 ? (
					<FormItem>
						<Alert message={updateErrors.join(' ')} type="error" />
					</FormItem>
				) : null}
				<FormItem
					label={isThirdPartySupplier ? 'Completed Notes' : 'Review'}
					name="note"
					rules={[
						{
							required: onlyReview,
							message: 'This field is required.',
						},
					]}
				>
					<Input.TextArea />
				</FormItem>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state, ownProps) => ({
	visible: ownProps.visible,
	onCancel: ownProps.onCancel,
	onSubmit: ownProps.onSubmit,
	createErrors: state.work_orders.createErrors,
	updateErrors: state.work_orders.updateErrors,
	creating: state.work_orders.creating,
	updating: state.work_orders.updating,
	currentUser: state.session.currentUser,
	onlyReview: ownProps.onlyReview,
	userType: state.session.userType,
});

const mapDispatchToProps = (dispatch) => ({
	getQuestionnaire: (problemTypeId) => dispatch(supplierGetQuestionnaire(problemTypeId)),
	submitReview: (entity) => dispatch(supplierSubmitReview(entity)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SupplierApproveReviewWorkForm);
